import _, { padStart } from "lodash";
import { defineStore } from "pinia";
import JobBuilder from "~/class/JobBuilderV1";
import { ReceiptOrderClass } from "~/class/ReceiptOrder";
import { JobAction } from "~/interface/Job";
import type { ReceiptPayment } from "~/interface/ReceiptPayment";
import type { Table } from "~/interface/Table";
import { computeOnTheHousePreVatBase } from "~/utils/onTheHouseBilling";
import dayjs from "dayjs";
import type { ReceiptOrderDynamodb } from "~/interface/ReceiptOrder";
import Swal from "sweetalert2";
import { registerResource, refreshResourceDebounced } from "~/services/repos/versionSync";
import { currentPlan } from "~/constants/packagePlan";

export type PaymentStatus = 'idle' | 'confirm' | 'complete';
export type PaymentType = string;

// Multiple Payment must hit the due amount exactly. Allow a small tolerance to
// absorb floating-point drift (e.g. 0.1 + 0.2 !== 0.3) so an exact split isn't
// rejected over a 0.00001 rounding error.
export const MULTIPLE_PAYMENT_TOLERANCE = 0.1;

// In a split (multiple) payment every payment type may repeat across lines
// (e.g. Cash 500 + Visa 250 + Visa 250) EXCEPT Cash, which is restricted to a
// single line — the fixed one at the top. This is the uuid we treat specially.
export const CASH_PAYMENT_UUID = 'Cash';

// Per-terminal "print copy receipt" preference. Persisted so it survives reloads
// AND so the single + multiple payment modals always agree on it. Historically
// only the single modal read/wrote this and the multi modal had no checkbox, so
// a stale flag checked once in the single modal leaked a COPY slip into a later
// split payment where nothing explained it. The store owns the key now — both
// modals go through hydrateCopyReceiptPref()/setCopyReceipt().
const COPY_RECEIPT_KEY = 'nl-payment-copy-receipt'

export interface PaymentOption {
  isCreditCard: boolean;
  uuid: string;
  row: number;
  onTheHouse: boolean;
  name: string;
  cardCharge?: number;
  customerId?: string;
}

type ResolvedPaymentOption = PaymentOption & {
  type: PaymentType;
}

export interface MultiplePaymentLine {
  id: string;
  type: PaymentType;
  amountInput: number;
}

interface AlertChangeState {
  show: boolean;
  amountChange: number;
  table: string;
}

export const usePosPaymentStore = defineStore("posPayment", () => {


  const tableInstanceStore = usePosTableInstanceStore();
  const employeeStore = usePosEmployeesStore();
  const shopStore = usePosShopStore();
  // receipts stores
  const receiptsStore = usePosReceiptsStore();
  const receiptIdCounterStore = usePosReceiptIdCounterStore();

  // const receiptSqsStore = useReceiptsSqsStore();
  //




  const isOpenModal = ref(false)
  const tableName = ref('A1')
  const tableAlias = ref('')
  const isClosingTable = ref(false)
  const isProcessingPayment = ref(false)
  const paymentOptions = computed<ResolvedPaymentOption[]>(() => {

    const result = useDataservice().paymentType.data.value || []

    let list = _.orderBy(
      result.map((item: Partial<PaymentOption> & { type?: string }) => normalizePaymentOption(item)),
      ['row'],
      ['asc']
    )

    // add CASH in Frst
    list.unshift({
      isCreditCard: false,
      uuid: 'Cash',
      row: 0,
      onTheHouse: false,
      name: 'Cash',
      type: 'Cash',
    })

    // Legacy parity (vm.payment.js:595-599): low packages (< 800) get a default
    // Credit Card option since they don't configure payment types on the server.
    // Appended last, matching legacy order: Cash, …serverData, Credit Card.
    if (currentPlan() < 800) {
      list.push({
        isCreditCard: true,
        uuid: 'Credit Card',
        row: 9999,
        onTheHouse: false,
        name: 'Credit Card',
        type: 'Credit Card',
      })
    }


    return list
  })

  // Options available in Multiple Payment mode. Entertain (onTheHouse) types are
  // excluded on purpose: an Entertain line discounts the whole bill to 0, which
  // has no meaning as one slice of a split payment. See the inline hint shown in
  // MultiPaymentModal so cashiers know why it's missing here.
  const multiplePaymentOptions = computed<ResolvedPaymentOption[]>(() =>
    paymentOptions.value.filter((option) => !option.onTheHouse)
  )

  const note = ref('')
  const room = ref('')
  const originalDueAmountPayment = ref(0)
  const dueAmountPayment = ref(0)
  const selectedPaymentOption = ref<ResolvedPaymentOption | null>(null)
  const inputAmount = ref(0)
  const isCopyReceipt = ref(false)
  const paymentStatus = ref<PaymentStatus>('idle')
  const changeAmount = ref(0)
  const tableHash = ref("1234");
  const isOpenMultiModal = ref(false)
  const multiplePayments = ref<MultiplePaymentLine[]>([])
  const alertChange = ref<AlertChangeState>({
    show: false,
    amountChange: 0,
    table: ""
  })
  let alertChangeTimeout: ReturnType<typeof setTimeout> | null = null
  // One-shot "click anywhere to dismiss" listener for the change toast (mirrors
  // the legacy FoodWeb behaviour where the cashier's next tap closes it).
  let alertChangeArmTimeout: ReturnType<typeof setTimeout> | null = null
  let alertChangeDismissHandler: (() => void) | null = null


  const isValidPayment = computed(() => {
    if (!selectedPaymentOption.value) return false
    if (selectedPaymentOption.value.onTheHouse) {
      return roundTo2(inputAmount.value) === 0
    }
    // A zero-due bill (e.g. 100% additional discount) is payable with 0 received.
    // Only block a negative input; the `< dueAmountPayment` check below already
    // rejects an under-payment when the bill actually owes money.
    if (inputAmount.value < 0) return false
    if (inputAmount.value < dueAmountPayment.value) return false
    return true
  })

  const totalMultipleInput = computed(() =>
    roundTo2(
      multiplePayments.value.reduce((sum, line) => sum + (line.amountInput || 0), 0)
    )
  )

  const remainingMultipleAmount = computed(() =>
    roundTo2(Math.max(0, dueAmountPayment.value - totalMultipleInput.value))
  )

  const multipleChangeAmount = computed(() =>
    roundTo2(Math.max(0, totalMultipleInput.value - dueAmountPayment.value))
  )

  // Cash is the only type allowed to overpay (the surplus becomes change), so we
  // track the cash and non-cash subtotals separately.
  const cashMultipleInput = computed(() =>
    roundTo2(
      multiplePayments.value
        .filter((line) => line.type === CASH_PAYMENT_UUID)
        .reduce((sum, line) => sum + (line.amountInput || 0), 0)
    )
  )

  const nonCashMultipleInput = computed(() =>
    roundTo2(
      multiplePayments.value
        .filter((line) => line.type !== CASH_PAYMENT_UUID)
        .reduce((sum, line) => sum + (line.amountInput || 0), 0)
    )
  )

  // Non-cash lines (card, transfer, etc.) may never exceed the grand total —
  // only Cash is allowed to run the total past the due amount.
  const isNonCashMultipleOverpaid = computed(() =>
    nonCashMultipleInput.value - dueAmountPayment.value > MULTIPLE_PAYMENT_TOLERANCE
  )

  // The bill is covered once the total reaches the due amount (Cash may push it
  // past, producing change). Uses the same tolerance for JS float drift.
  const isMultipleFullyCovered = computed(() =>
    totalMultipleInput.value - dueAmountPayment.value >= -MULTIPLE_PAYMENT_TOLERANCE
  )

  // Total is past the due amount beyond tolerance — overpay, must be blocked.
  const isMultipleOverpaid = computed(() =>
    totalMultipleInput.value - dueAmountPayment.value > MULTIPLE_PAYMENT_TOLERANCE
  )

  // Total matches the due amount within tolerance (the only valid state).
  const isMultipleAmountBalanced = computed(() =>
    Math.abs(totalMultipleInput.value - dueAmountPayment.value) <= MULTIPLE_PAYMENT_TOLERANCE
  )

  // Only Cash must be unique. Every other payment type may repeat across lines,
  // so this flags a bill only when TWO+ lines are Cash.
  const hasDuplicateMultiplePaymentTypes = computed(() =>
    multiplePayments.value.filter((line) => line.type === CASH_PAYMENT_UUID).length > 1
  )

  const canAddMultiplePaymentLine = computed(() => pickTypeForNewMultipleLine() !== null)

  const isValidMultiplePayment = computed(() => {
    if (multiplePayments.value.length === 0) return false
    if (dueAmountPayment.value <= 0) return false

    const hasInvalidLine = multiplePayments.value.some((line) => {
      if (!line.type) return true
      if (line.amountInput <= 0) return true
      return false
    })

    if (hasInvalidLine) return false
    if (hasDuplicateMultiplePaymentTypes.value) return false
    // Non-cash lines must never exceed the grand total — only Cash may overpay
    // (the surplus becomes change). Reject if the non-cash subtotal runs over.
    if (isNonCashMultipleOverpaid.value) return false
    // The bill must be fully covered. Cash is allowed to push the total past the
    // due amount; the excess is returned as change.
    return isMultipleFullyCovered.value
  })

  function roundTo2(value: number) {
    return Math.round((value + Number.EPSILON) * 100) / 100
  }

  function normalizePaymentOption(item: Partial<PaymentOption> & { type?: string }): ResolvedPaymentOption {
    const uuid = `${item.uuid || item.type || item.name || 'payment'}`

    return {
      isCreditCard: !!item.isCreditCard,
      uuid,
      row: Number(item.row || 0),
      onTheHouse: !!item.onTheHouse,
      name: item.name || uuid,
      cardCharge: Number(item.cardCharge || 0),
      customerId: `${item.customerId || ""}`,
      type: uuid
    }
  }

  function createMultiplePaymentLine(type?: PaymentType, amountInput = 0): MultiplePaymentLine {
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: type || paymentOptions.value[0]?.uuid || "Cash",
      amountInput: roundTo2(Math.max(0, amountInput))
    }
  }

  function getOriginalDueAmount() {
    return roundTo2(Math.max(0, originalDueAmountPayment.value))
  }

  function syncDueAmountWithSelectedPayment() {
    dueAmountPayment.value = selectedPaymentOption.value?.onTheHouse ? 0 : getOriginalDueAmount()
    changeAmount.value = Math.max(0, inputAmount.value - dueAmountPayment.value)
  }

  function buildOnTheHouseSinglePayment(payment: ReceiptPayment): ReceiptPayment {
    const paymentName = selectedPaymentOption.value?.name || payment.list[0]?.name || 'Cash'
    const paymentId = selectedPaymentOption.value?.uuid || payment.list[0]?.paymentId || paymentName
    const originalDueAmount = getOriginalDueAmount()

    return {
      ...payment,
      isOnTheHouse: true,
      list: [
        {
          ...payment.list[0],
          amount: 0,
          amountInput: 0,
          entertainAmount: originalDueAmount,
          entertainMode: paymentName,
          isEntertain: true,
          name: paymentName,
          paymentId
        }
      ]
    }
  }

  function applyOnTheHouseBilling(table: Table) {
    const billing = _.cloneDeep(table.billing || {})
    const vat = Array.isArray(billing.vat) ? [...billing.vat] : [0, 0, 0]

    vat[2] = 0

    // dc2[2] must be the exact PRE-VAT base so the comped receipt's
    // BeforeVatV2 nets to 0 and the breakdown reconciles.
    const preVatBase = computeOnTheHousePreVatBase(billing)

    // An On-The-House bill is paid with 0, so it carries no card charge: zero cc
    // (and exclude it from preVatBase) — this keeps BeforeVatV2 = 0.
    table.billing = {
      ...billing,
      dc2: [0, 100, preVatBase],
      cc: [0, 0, 0],
      vat,
      rounding: 0,
      roundingMode: 'none',
      grandTotal: 0
    }
  }

  function setDueAmount(amount: number) {
    originalDueAmountPayment.value = roundTo2(Math.max(0, amount))
    syncDueAmountWithSelectedPayment()
    if (isOpenMultiModal.value && multiplePayments.value.length === 0) {
      resetMultiplePayment()
    }
  }

  function selectPaymentOption(option: ResolvedPaymentOption) {
    selectedPaymentOption.value = option
    paymentStatus.value = 'idle'
    if (!isOpenMultiModal.value) {
      syncDueAmountWithSelectedPayment()
    }
  }

  function setInputAmount(amount: number) {
    inputAmount.value = amount
    changeAmount.value = Math.max(0, amount - dueAmountPayment.value)

  }

  function validateAndConfirm() {
    if (isProcessingPayment.value) return false
    if (paymentStatus.value === 'complete') return false
    if (!isValidPayment.value) {
      return false
    }
    paymentStatus.value = 'confirm'
    return true
  }


  //single payment
  function completePayment() {
    if (paymentStatus.value !== 'confirm') {
      return false
    }
    if (isProcessingPayment.value) {
      return false
    }
    isProcessingPayment.value = true
    paymentStatus.value = 'complete'

    const receipt = processPayment('single')

    setTimeout(() => {
      tableInstanceStore.billFilter = 'all'
      closeRequest()
      closeMultipleRequest()
      isProcessingPayment.value = false
    }, 150)

     
    isClosingTable.value ? usePosTableStatesStore().clearTableState(tableAlias.value) : null

    const paidTableName =
      tableInstanceStore.data?.nickName ||
      tableInstanceStore.data?.alias ||
      tableInstanceStore.data?.name ||
      tableName.value ||
      ""

    toggleAlertChange(changeAmount.value, paidTableName)

    //return for print with plugin
    return receipt
  }


  // todo : receipt must implemnt multiple payments
  function completeMultiplePayment() {
    if (paymentStatus.value !== 'confirm') {
      return false
    }

    if (!isValidMultiplePayment.value) {
      return false
    }

    if (isProcessingPayment.value) {
      return false
    }
    isProcessingPayment.value = true
    paymentStatus.value = 'complete'
    const receipt = processPayment('multiple')



    setTimeout(() => {
      tableInstanceStore.billFilter = 'all'
      closeRequest()
      closeMultipleRequest()
      isProcessingPayment.value = false
    }, 150)
    
    isClosingTable.value ? usePosTableStatesStore().clearTableState(tableName.value) : null

    const paidTableName =
      tableInstanceStore.data?.nickName ||
      tableInstanceStore.data?.alias ||
      tableInstanceStore.data?.name ||
      tableName.value ||
      ""

    // Change in the split flow comes from a Cash overpay (non-cash lines can't
    // overpay), so surface the multiple-payment change, not the single-payment ref.
    toggleAlertChange(multipleChangeAmount.value, paidTableName)



    return receipt
  }

  function validateAndConfirmMultiplePayment() {
    if (isProcessingPayment.value) return false
    if (paymentStatus.value === 'complete') return false
    if (!isValidMultiplePayment.value) {
      return false
    }

    paymentStatus.value = 'confirm'
    return true
  }

  function processPayment(mode: 'single' | 'multiple' = 'single') {

    //todo : 
    if (false && tableHash.value !== tableInstanceStore.data?.hash) {
      Swal.fire({
        title: "Table has been modified",
        text: "โต๊ะมีข้อมูลที่ถูกแก้ไขใหม่",
        icon: "warning"
      });
      return;
    }

    const payment = generatePayment(mode)
    const isSingleOnTheHouse = mode === 'single' && !!selectedPaymentOption.value?.onTheHouse

    const _customerId = shopStore.getStoreId

    // Shift is derived from the job chain (count of CHANGE_SHIFT jobs + 1).
    const _shiftWork = usePosJobsStore().getShift()
    const _user = employeeStore.getCurrentEmployee?.name || 'admin'
    const _payment = isSingleOnTheHouse ? buildOnTheHouseSinglePayment(payment) : payment

 
    const tableCloned: Table = _.cloneDeep(tableInstanceStore.data);
    tableCloned.items = tableInstanceStore.itemsDisplay
    tableCloned.billing = _.cloneDeep(tableInstanceStore.summaryBillingMoney)
    tableCloned.endDateTime = dayjs().format("YYYY-MM-DDTHH:mm:ss");
    // Guest count lives in tableStates (via the merged `table` getter), not on
    // `data`. Carry it onto the clone so ReceiptOrder.fromTable → CustomerCount
    // is populated; otherwise the receipt always prints Customer = 0. Mode gating
    // (CustomerMode off ⇒ drop the field) is handled later in the print plugin.
    tableCloned.guests = tableInstanceStore.table?.guests ?? tableCloned.guests ?? 0;

    if (isSingleOnTheHouse) {
      applyOnTheHouseBilling(tableCloned)
    }


    // const receiptOrder = ReceiptOrderClass
    //   .fromTable(tableCloned, {
    //     orderId: _orderId,
    //     shiftWork: _shiftWork,
    //     user: _user,
    //     payment: _payment,
    //     customerId: _customerId
    //   })
    //   .toJSON();



    const jobchainStore = usePosJobsStore();
    const _businessDate = jobchainStore.getBusinessDateFromTimestamp(tableCloned.endDateTime)

    // One nonce shared by the bill AND its counter bump (same format as the
    // ReceiptOrderClass default) — ties receipt, queue item and Firestore
    // idempotency doc together.
    const _nonce = Math.random().toString(36).substring(2, 15)
    const _pureId = receiptIdCounterStore.getNextReceiptIdAndIncrement(_nonce)
    // The prefix is the BUSINESS month, not the calendar month — a BusinessMode-1
    // shop selling at 01:00 on the 1st still belongs to the previous month. Derived
    // from _businessDate so the printed id can never disagree with the receipt's
    // own BusinessDate below.
    const _receiptId = `${_businessDate.substring(0, 7).replace("-", "")}-${padStart(_pureId.toString(), 4, "0")}`
    const _orderId = parseInt(_receiptId.replace('-', ''))

    const receiptOrder = ReceiptOrderClass.fromTable(tableCloned, {
      orderId: _orderId,
      shiftWork: _shiftWork,
      user: _user,
      payment: _payment,
      customerId: _customerId,
      printMode: 'payment'
    })
    receiptOrder.BusinessDate = _businessDate
    receiptOrder.Nonce = _nonce // same nonce the counter claim was made with
    const receiptOrderDynamodb: ReceiptOrderDynamodb = receiptOrder.toDynamodb();

    // Split-bill title suffix (→ "Table A5-2"). billFilter is still the selected
    // bill here (it's reset to 'all' only in the post-payment setTimeout). Extra
    // field only; Table stays "A5" so reports/grouping are unaffected.
    ;(receiptOrderDynamodb as any).SplitBillNo = tableInstanceStore.selectedSplitBillNo

    console.log("receiptOrder=>", receiptOrderDynamodb);

    //JOBS
    const jobData = createJobchain(receiptOrderDynamodb, _payment)
    jobchainStore.add(jobData, true)

    //Receipts
    receiptsStore.add(receiptOrderDynamodb)

    return receiptOrderDynamodb

  }

  function generatePayment(mode: 'single' | 'multiple' = 'single'): ReceiptPayment {
    if (mode === 'multiple') {
      return generateMultiplePayment()
    }

    return generateSinglePayment()
  }

  function generateSinglePayment(): ReceiptPayment {
    const selectedOption = selectedPaymentOption.value
    const paymentName = selectedOption?.name || paymentOptions.value[0]?.name || 'Cash'
    const isOnTheHouse = !!selectedOption?.onTheHouse
    const originalDueAmount = getOriginalDueAmount()

    return {
      list: [
        {
          amount: isOnTheHouse ? 0 : dueAmountPayment.value,
          amountInput: isOnTheHouse ? 0 : inputAmount.value,
          entertainAmount: isOnTheHouse ? originalDueAmount : 0,
          entertainMode: isOnTheHouse ? paymentName : null,
          isEntertain: isOnTheHouse,
          name: paymentName,
          paymentId: selectedOption?.uuid || paymentName
        }
      ],
      note: note.value || '',
      room: room.value || '',
      isOnTheHouse
    }
  }

  function generateMultiplePayment(): ReceiptPayment {
    const due = dueAmountPayment.value

    // Non-cash lines always settle their full tendered amount (validation
    // guarantees their sum never exceeds the bill). Cash only settles whatever
    // is left of the bill after them — the surplus the customer handed over is
    // change (display-only) and must NOT inflate the receipt's cash amount.
    const nonCashTotal = roundTo2(
      multiplePayments.value
        .filter((line) => line.amountInput > 0 && line.type !== CASH_PAYMENT_UUID)
        .reduce((sum, line) => sum + line.amountInput, 0)
    )
    const cashApplied = roundTo2(Math.max(0, due - nonCashTotal))

    const paymentList = multiplePayments.value
      .filter((line) => line.amountInput > 0)
      .map((line) => {
        const option = paymentOptions.value.find((item) => item.uuid === line.type)
        const isCash = line.type === CASH_PAYMENT_UUID
        // Cash records only the remainder it settles; every other type records its
        // full amount. Sum of all `amount`s therefore equals the Grand Total.
        const appliedAmount = isCash ? cashApplied : roundTo2(line.amountInput)
        const isOnTheHouse = !!option?.onTheHouse
        const paymentName = option?.name || line.type

        return {
          amount: appliedAmount,
          amountInput: roundTo2(line.amountInput),
          entertainAmount: isOnTheHouse ? appliedAmount : 0,
          entertainMode: isOnTheHouse ? paymentName : null,
          isEntertain: isOnTheHouse,
          name: paymentName,
          paymentId: option?.uuid || line.type
        }
      })

    return {
      list: paymentList,
      note: note.value || '',
      room: room.value || '',
      isOnTheHouse: paymentList.some((item) => item.isEntertain)
    }
  }



  function createJobchain(receipt: ReceiptOrderDynamodb, _payment) {
    let items = tableInstanceStore.itemsDisplay;
    const employeeStore = usePosEmployeesStore();
    const employee = employeeStore?.getCurrentEmployee;

    const json2 = items.reduce((acc: any, item: any) => {
      acc[item.uuid] = item.quantity * -1
      return acc
    }, {})



    let _paymentType = _payment.list[0].name
    if(_payment.list.length > 1) { 
      console.log('Multiple payment detected', _payment)
      _paymentType = _payment.list.map((item: any) => `${item.name} ${item.amount}`).join(', ')
    }
    

    //check items is equal all items in table
    const _isClosingTable = items.length === tableInstanceStore.data?.items?.length
    isClosingTable.value = _isClosingTable

    return new JobBuilder()
      .withActionType(JobAction.PAY)
      .withTableAlias(tableInstanceStore.selectedTableId)
      .withTableUuid(tableInstanceStore.selectedTableId)
      .withEmployee(employee)
      .withMode(1)
      .withIsClosingTable(_isClosingTable)
      .withJson2(json2)
      .withJobIdAndTimestamp(useServerTime().nowISO())
      .withCustom({
        grandTotal: receipt.GrandTotal,
        paymentType:_paymentType,
        nickName : tableInstanceStore.data?.nickName || null,
        // Paying the full bill empties the table — same "table is now closed"
        // flag that MOVE/VOID carry, so all three empty-table paths tag the job
        // consistently. State clearing itself already runs via isClosingTable.
        isTableClosed: _isClosingTable,
        // Client-generated receipt id (same value as receipt.Nonce / Ref1 / Key /
        // UniqueId / Uuid). The PAY job's `timestamp` and the receipt's `Date` are
        // two independent clock reads (server-offset UTC vs local endDateTime), so
        // the Nonce is the ONLY reliable receipt↔job link. Carried so the open-menu
        // tracker can stamp it on its DynamoDB row and the server can void those
        // rows when the bill is voided. See job.to.openMenu.plugin.ts.
        receiptNonce: receipt.Nonce || receipt.Ref1 || "",
        __items: items,
      })
      .build()
  }

  function readCopyReceiptPref(): boolean {
    try {
      return localStorage.getItem(COPY_RECEIPT_KEY) === 'true'
    } catch {
      return false
    }
  }

  // Load the persisted preference into the store. Call this whenever a payment
  // modal opens (single OR multiple) so both flows reflect the same setting.
  function hydrateCopyReceiptPref() {
    isCopyReceipt.value = readCopyReceiptPref()
  }

  // Set + persist. The only writer of the preference — both modals' checkboxes
  // route through here so the value stays in sync across single/multiple.
  function setCopyReceipt(value: boolean) {
    isCopyReceipt.value = !!value
    try {
      localStorage.setItem(COPY_RECEIPT_KEY, String(!!value))
    } catch {}
  }

  function resetPayment() {
    paymentStatus.value = 'idle'
    inputAmount.value = 0
    isCopyReceipt.value = false
    changeAmount.value = 0
    selectedPaymentOption.value = paymentOptions.value[0] || null
    originalDueAmountPayment.value = 0
    dueAmountPayment.value = 0
    note.value = ''
    room.value = ''
    isOpenModal.value = false
    isOpenMultiModal.value = false
    isProcessingPayment.value = false
    resetMultiplePayment()
  }

  function resetMultiplePayment() {
    // Pre-seed two rows: most splits are two payment types in one bill, so we
    // skip the manual "Add" step. Row 1 = Cash (the first option). Row 2 = the
    // next available type, or Cash again when Cash is the only option — that
    // duplicate is intentional and blocks completion (see
    // `hasDuplicateMultiplePaymentTypes`) until the cashier picks a real type.
    const firstType = multiplePaymentOptions.value[0]?.uuid || "Cash"
    const secondType = multiplePaymentOptions.value[1]?.uuid || firstType
    multiplePayments.value = [
      createMultiplePaymentLine(firstType),
      createMultiplePaymentLine(secondType),
    ]
  }

  // Choose a default type for a freshly-added line. Prefer any non-Cash option
  // (those may repeat, so there's always one available). Fall back to Cash only
  // when it hasn't been used yet — Cash is limited to a single line. Returns
  // null when no line can be added (only Cash exists and it's already used).
  function pickTypeForNewMultipleLine(): PaymentType | null {
    const nonCashOption = multiplePaymentOptions.value.find((option) => option.uuid !== CASH_PAYMENT_UUID)
    if (nonCashOption) return nonCashOption.uuid

    const cashUsed = multiplePayments.value.some((line) => line.type === CASH_PAYMENT_UUID)
    return cashUsed ? null : CASH_PAYMENT_UUID
  }

  function addMultiplePaymentLine() {
    const type = pickTypeForNewMultipleLine()
    if (!type) {
      return false
    }

    multiplePayments.value.push(createMultiplePaymentLine(type))
    return true
  }

  function removeMultiplePaymentLine(id: string) {
    if (multiplePayments.value.length <= 1) {
      return
    }

    multiplePayments.value = multiplePayments.value.filter((line) => line.id !== id)
  }

  function updateMultiplePaymentLineType(id: string, type: PaymentType) {
    // Only Cash is capped at one line; any other type may repeat freely.
    if (type === CASH_PAYMENT_UUID) {
      const cashUsedElsewhere = multiplePayments.value.some(
        (item) => item.id !== id && item.type === CASH_PAYMENT_UUID
      )
      if (cashUsedElsewhere) {
        return false
      }
    }

    const line = multiplePayments.value.find((item) => item.id === id)
    if (!line) return false

    line.type = type
    return true
  }

  function updateMultiplePaymentLineAmount(id: string, amount: number) {
    const line = multiplePayments.value.find((item) => item.id === id)
    if (!line) return
    line.amountInput = roundTo2(Math.max(0, amount))
  }

  /**
   * Register the `paymentType` resource with versionSync. Called by bootstrap
   * during Phase A (alongside the other stores' `init()`) so the paymentType
   * data is fetched / reconciled in the single syncAllVersions pass — same
   * pattern as products, settings, employees, customerInfo.
   */
  function init() {
    const paymentEntity = useDataservice().paymentType;
    registerResource({
      resource: 'paymentType',
      refetch: async () => {
        await paymentEntity.fetch();
        return paymentEntity.data.value;
      },
      applyToStore: (data: any) => {
        if (data != null) paymentEntity.data.value = data;
      },
    });
  }

  /** Refresh + bump hash after a successful paymentType mutation. Debounced
   *  so rapid edits collapse into one refetch + Firestore bump. */
  function refresh() {
    refreshResourceDebounced('paymentType');
  }

  function restart() {
    resetPayment()
  }

  function openRequest() {
    resetPayment();
    setDueAmount(tableInstanceStore.summaryBillingMoney?.grandTotal || 0)
    tableName.value = tableInstanceStore.data?.nickName || tableInstanceStore.data?.alias || tableInstanceStore.data?.name || ""
    tableAlias.value =   tableInstanceStore.data?.alias || tableInstanceStore.data?.name || ""
    isOpenModal.value = true;
  }

  function closeRequest() {
    isOpenModal.value = false;
  }

  function openMultipleRequest() {
    const summaryGrandTotal = roundTo2(tableInstanceStore.summaryBillingMoney?.grandTotal || 0)
    originalDueAmountPayment.value = summaryGrandTotal
    dueAmountPayment.value = summaryGrandTotal

    tableName.value = tableInstanceStore.data?.alias || tableInstanceStore.data?.name || tableName.value
    tableAlias.value =    tableInstanceStore.data?.alias || tableInstanceStore.data?.name || ""
    paymentStatus.value = 'idle'

    resetMultiplePayment()

    const firstLine = multiplePayments.value[0]
    if (firstLine) {
      firstLine.type = selectedPaymentOption.value?.uuid || firstLine.type
      if (inputAmount.value > 0 && totalMultipleInput.value === 0) {
        firstLine.amountInput = roundTo2(inputAmount.value)
      }
    }

    isOpenModal.value = false
    isOpenMultiModal.value = true
  }

  function switchToSinglePayment() {
    const firstLine = multiplePayments.value[0]
    if (firstLine) {
      let selected = paymentOptions.value.find((option) => option.uuid === firstLine.type)
      if (selected) {
        selectPaymentOption(selected)
      }
      setInputAmount(selected?.onTheHouse ? 0 : firstLine.amountInput)
    }

    paymentStatus.value = 'idle'
    isOpenMultiModal.value = false
    syncDueAmountWithSelectedPayment()
    isOpenModal.value = true
  }

  function closeMultipleRequest() {
    isOpenMultiModal.value = false
  }

  function hideAlertChange() {
    alertChange.value = {
      show: false,
      amountChange: 0,
      table: ""
    }
    if (alertChangeTimeout) {
      clearTimeout(alertChangeTimeout)
      alertChangeTimeout = null
    }
    if (alertChangeArmTimeout) {
      clearTimeout(alertChangeArmTimeout)
      alertChangeArmTimeout = null
    }
    if (alertChangeDismissHandler && typeof document !== "undefined") {
      document.removeEventListener("click", alertChangeDismissHandler)
    }
    alertChangeDismissHandler = null
  }

  function toggleAlertChange(amount: number, paidTable = "") {
    // Reset any previous toast (timer + click listener) before showing a new one.
    hideAlertChange()

    alertChange.value = {
      // Round to 2 decimals — the raw change can carry float drift (e.g.
      // 28.230000000000018) that would otherwise print in the toast.
      amountChange: roundTo2(Number(amount) || 0),
      show: true,
      table: paidTable
    }

    // Auto-hide after 30s (matches the legacy FoodWeb change toast, trimmed from
    // its 45s). The cashier can dismiss earlier by clicking anywhere — see below.
    alertChangeTimeout = setTimeout(() => {
      hideAlertChange()
    }, 30 * 1000)

    // Click-anywhere-to-dismiss, mirroring the legacy behaviour. Arm on the NEXT
    // tick so the same click/tap that completed the payment doesn't instantly
    // close the toast. { once: true } removes the listener after it fires.
    if (typeof document !== "undefined") {
      alertChangeDismissHandler = () => hideAlertChange()
      alertChangeArmTimeout = setTimeout(() => {
        alertChangeArmTimeout = null
        if (alertChangeDismissHandler) {
          document.addEventListener("click", alertChangeDismissHandler, { once: true })
        }
      }, 150)
    }
  }

  function getAvailablePaymentTypesForLine(id: string): ResolvedPaymentOption[] {
    // Every payment type may repeat across lines, so the dropdown offers them
    // all — EXCEPT Cash, which is hidden once another line already holds it.
    const cashUsedByOtherLine = multiplePayments.value.some(
      (line) => line.id !== id && line.type === CASH_PAYMENT_UUID
    )

    return multiplePaymentOptions.value.filter(
      (option) => option.uuid !== CASH_PAYMENT_UUID || !cashUsedByOtherLine
    )
  }

  return {
    paymentOptions,
    multiplePaymentOptions,
    dueAmountPayment,
    selectedPaymentOption,
    inputAmount,
    isCopyReceipt,
    hydrateCopyReceiptPref,
    setCopyReceipt,
    paymentStatus,
    changeAmount,
    alertChange,
    hideAlertChange,
    isValidPayment,
    setDueAmount,
    selectPaymentOption,
    setInputAmount,
    validateAndConfirm,
    validateAndConfirmMultiplePayment,
    completePayment,
    completeMultiplePayment,
    resetPayment,
    init,
    restart,
    tableName,
    isOpenModal,
    isOpenMultiModal,
    openRequest,
    openMultipleRequest,
    closeMultipleRequest,
    switchToSinglePayment,
    closeRequest,
    note,
    room,
    multiplePayments,
    totalMultipleInput,
    remainingMultipleAmount,
    multipleChangeAmount,
    cashMultipleInput,
    nonCashMultipleInput,
    isNonCashMultipleOverpaid,
    isMultipleFullyCovered,
    isMultipleOverpaid,
    isMultipleAmountBalanced,
    hasDuplicateMultiplePaymentTypes,
    canAddMultiplePaymentLine,
    isValidMultiplePayment,
    addMultiplePaymentLine,
    removeMultiplePaymentLine,
    updateMultiplePaymentLineType,
    updateMultiplePaymentLineAmount,
    getAvailablePaymentTypesForLine,
    processPayment,
    isProcessingPayment,
    refresh,
  }

});
